import { createContext, useContext, useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { getSessionItem, removeSessionItem } from "@/utils/storage";
import { trueMobile } from "@/utils/platform";

type AuthContextType = {
  user: any | null;
  token: string | null;
  configuracion: any | null;
  setUser: (user: any | null) => void;
  setToken: (token: string | null) => void;
  logout: () => void;
  isMobileApp: boolean;
  fetchConfiguracion: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | null>(null);

function getTenantId() {
  return localStorage.getItem('tenant_codigo') || localStorage.getItem('x-tenant-id') || '';
}
async function apiFetch(url: string, options: RequestInit = {}) {
  const token = localStorage.getItem('token');
  const tenantId = getTenantId();
  const headers: any = {
...(options.headers || {}),
...(token? { Authorization: `Bearer ${token}` } : {}),
...(tenantId? { 'x-tenant-id': tenantId } : {}),
  };
  if (!(options.body instanceof FormData) &&!headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  return fetch(url, {...options, headers });
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any | null>(() => {
    const raw = getSessionItem("user");
    return raw && raw!=="undefined"? JSON.parse(raw) : null;
  });
  const [token, setToken] = useState<string | null>(() => {
    const raw = getSessionItem("token");
    return raw && raw!=="undefined"? raw : null;
  });
  const [configuracion, setConfiguracion] = useState<any | null>(null);
  const backend_api = import.meta.env.VITE_API_URL_BACKEND;

  const fetchConfiguracion = async () => {
    if (!token ||!user) return;
    const rolId = user?.rol;
    if (Number(rolId)===1) { setConfiguracion(null); return; }
    try {
      const response = await apiFetch(`${backend_api}/configuracion-empresa/empresa/`);
      if (response.ok) {
        const data = await response.json();
        if (data && data.length > 0) setConfiguracion(data[0]);
      }
    } catch (error) {
      console.error("Error fetching configuracion:", error);
    }
  };

  useEffect(() => {
    if (user && token) fetchConfiguracion();
    else setConfiguracion(null);
  }, [user, token]);

  const logout = () => {
    removeSessionItem("user");
    removeSessionItem("token");
    localStorage.removeItem("token");
    localStorage.removeItem("tenant_codigo");
    setUser(null);
    setToken(null);
    setConfiguracion(null);
  };

  const platform = Capacitor.getPlatform();
  const isMobileApp = platform==="ios" || platform==="android" || trueMobile;

  return (
    <AuthContext.Provider value={{ user, token, configuracion, setUser, setToken, logout, isMobileApp, fetchConfiguracion }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};